import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBookBible, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useSermon, useAuth, useDisableScrollSnap } from "../../util/hook";
import Header from "../organisms/Header";
import Footer from "../organisms/Footer";
import Pagination from "../atoms/Pagination";
import { TagBadgeStyle } from "../../style/SermonStyles";

const SermonList = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const { sermons: sermonData, loading, error } = useSermon();
  const { isLoggedIn } = useAuth();
  useDisableScrollSnap();

  const ITEMS_PER_PAGE = 10;

  const filteredSermons =
    activeTab === "all"
      ? sermonData
      : sermonData.filter((sermon) => sermon.category === activeTab);

  const totalPages = Math.ceil(filteredSermons.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedSermons = filteredSermons.slice(startIndex, endIndex);

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
    setCurrentPage(1); // 탭 변경 시 페이지 리셋
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0); // 상단으로 스크롤
  };

  const handleSermonClick = (sermonId: number) => {
    navigate(`/sermon/${sermonId}`, { state: { backTo: "/sermons" } });
  };

  // 데이터 삭제 후 빈 페이지 처리
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <>
      <Header />
      <Container>
        <BackButton onClick={() => navigate("/")}>
          <FontAwesomeIcon icon={faArrowLeft} /> 돌아가기
        </BackButton>

        <PageHeader>
          <PageTitle>
            <FontAwesomeIcon icon={faBookBible} /> 설교 전체보기
          </PageTitle>
          <PageTitleEng>SERMON LIST</PageTitleEng>
          <PageDesc>하나님의 말씀을 듣고, 믿고, 누리며 살아갑니다.</PageDesc>
        </PageHeader>

        <TabMenu>
          <TabBtn
            className={activeTab === "all" ? "active" : ""}
            onClick={() => handleTabClick("all")}
          >
            전체보기
          </TabBtn>
          <TabBtn
            className={activeTab === "sunday" ? "active" : ""}
            onClick={() => handleTabClick("sunday")}
          >
            주일예배
          </TabBtn>
          <TabBtn
            className={activeTab === "friday" ? "active" : ""}
            onClick={() => handleTabClick("friday")}
          >
            금요모임
          </TabBtn>
          <TabBtn
            className={activeTab === "special" ? "active" : ""}
            onClick={() => handleTabClick("special")}
          >
            특별집회
          </TabBtn>
          <TabBtn
            className={activeTab === "seasonal" ? "active" : ""}
            onClick={() => handleTabClick("seasonal")}
          >
            절기
          </TabBtn>
        </TabMenu>

        {!loading && !error && filteredSermons.length > 0 && (
          <ResultCount>총 {filteredSermons.length}개의 설교</ResultCount>
        )}

        {isLoggedIn && (
          <WriteButtonWrapper>
            <WriteButton
              onClick={() => navigate("/sermon/new", { replace: true })}
            >
              글쓰기
            </WriteButton>
          </WriteButtonWrapper>
        )}

        {loading && <LoadingMessage>설교를 불러오는 중...</LoadingMessage>}

        {error && (
          <ErrorMessage>설교를 불러오는 중 오류가 발생했습니다.</ErrorMessage>
        )}

        {!loading && !error && filteredSermons.length === 0 && (
          <EmptyMessage>
            등록된 설교가 없습니다.
            {isLoggedIn && (
              <EmptyActionButton
                onClick={() => navigate("/sermon/new", { replace: true })}
              >
                첫 설교 등록하기
              </EmptyActionButton>
            )}
          </EmptyMessage>
        )}

        {!loading && !error && filteredSermons.length > 0 && (
          <>
            <SermonTable>
              <thead>
                <tr>
                  <th className="col-cate">구분</th>
                  <th className="col-title">설교제목</th>
                  <th className="col-scripture">본문</th>
                  <th className="col-date">날짜</th>
                  <th className="col-preacher">설교자</th>
                </tr>
              </thead>
              <tbody>
                {paginatedSermons.map((sermon) => (
                  <SermonRow
                    key={sermon.id}
                    onClick={() => handleSermonClick(sermon.id)}
                  >
                    <td>
                      <TagBadge className={`tag-${sermon.category}`}>
                        {sermon.categoryName}
                      </TagBadge>
                    </td>
                    <td className="col-title">{sermon.title}</td>
                    <td className="col-scripture">
                      {sermon.scripture.join(" / ")}
                    </td>
                    <td className="col-date">{sermon.date}</td>
                    <td>{sermon.preacher}</td>
                  </SermonRow>
                ))}
              </tbody>
            </SermonTable>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </Container>
      <Footer />
    </>
  );
};

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: calc(100vh - 200px);

  @media (max-width: 768px) {
    padding: 30px 15px;
  }

  @media (max-width: 480px) {
    padding: 20px 10px;
  }
`;

const BackButton = styled.button`
  background: #f5f5f5;
  border: 1px solid #ddd;
  padding: 12px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  margin-bottom: 30px;
  transition: 0.3s;

  &:hover {
    background: #e0e0e0;
  }

  svg {
    margin-right: 8px;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;

  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const PageTitle = styled.h1`
  font-size: 36px;
  color: #004094;
  margin-bottom: 5px;
  letter-spacing: -1px;
  line-height: 1.2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-weight: 700;

  svg {
    font-size: 0.9em;
  }

  @media (max-width: 768px) {
    font-size: 28px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    gap: 8px;
  }
`;

const PageTitleEng = styled.div`
  font-size: 14px;
  color: #999;
  margin-bottom: 20px;
  letter-spacing: 2px;
  text-transform: uppercase;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const PageDesc = styled.p`
  font-size: 18px;
  color: #555;
  font-weight: 300;
  word-break: keep-all;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const TabMenu = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
  border-bottom: 1px solid #ddd;

  @media (max-width: 768px) {
    justify-content: flex-start;
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
    padding: 0 10px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  @media (max-width: 480px) {
    padding: 0 5px;
  }
`;

const TabBtn = styled.div`
  padding: 15px 30px;
  font-size: 16px;
  color: #888;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: 0.3s;
  font-weight: 700;
  flex-shrink: 0;

  &:hover,
  &.active {
    color: #004094;
    border-bottom-color: #004094;
  }

  @media (max-width: 768px) {
    padding: 12px 20px;
    font-size: 14px;
    white-space: nowrap;
  }

  @media (max-width: 480px) {
    padding: 10px 15px;
    font-size: 12px;
    min-width: 70px;
    text-align: center;
  }
`;

const ResultCount = styled.div`
  text-align: right;
  font-size: 14px;
  color: #666;
  margin-bottom: 15px;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const WriteButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 20px;
`;

const WriteButton = styled.button`
  padding: 10px 20px;
  background: #004094;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 700;

  &:hover {
    background: #003070;
  }
`;

const SermonTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: center;
  font-size: 15px;

  thead th {
    padding: 15px;
    background: #f4f4f4;
    color: #333;
    border-top: 2px solid #333;
    border-bottom: 1px solid #ccc;
    font-weight: 700;
  }

  tbody td {
    padding: 6px 15px;
    border-bottom: 1px solid #eee;
    color: #555;
    font-weight: 300;
  }

  tbody tr:hover {
    background: #f9fbfd;
  }

  .col-cate {
    width: 10%;
    color: #004094;
  }

  .col-title {
    width: 40%;
    text-align: left;
    padding-left: 30px !important;
    color: #222;
    cursor: pointer;
    font-weight: 700;
  }

  .col-scripture {
    width: 20%;
    color: #666;
    font-size: 14px;
  }

  .col-date {
    width: 15%;
    color: #888;
    font-size: 14px;
  }

  .col-preacher {
    width: 15%;
    font-weight: 700;
  }

  @media (max-width: 1024px) {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
    font-size: 14px;
  }

  @media (max-width: 768px) {
    font-size: 12px;

    thead th {
      padding: 10px 8px;
    }

    tbody td {
      padding: 6px 8px;
    }

    .col-title {
      padding-left: 15px !important;
    }
  }
`;

const SermonRow = styled.tr`
  cursor: pointer;
  transition: 0.3s;

  &:hover {
    background: #f9f9f9;
  }

  &:hover .col-title {
    color: #004094;
  }
`;

const TagBadge = styled.span`
  ${TagBadgeStyle}
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 16px;
  color: #666;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 16px;
  color: #c33;
  background-color: #fee;
  border-radius: 4px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 16px;
  color: #666;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const EmptyActionButton = styled.button`
  padding: 12px 24px;
  background: #004094;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 700;
  font-size: 14px;

  &:hover {
    background: #003070;
  }
`;

export default SermonList;
